// src/hooks/useTaskForm.js
/**
 * useTaskForm — encapsulates form state, validation, and touch tracking.
 *
 * Separating this from TaskForm.jsx means the validation logic
 * is independently testable and reusable (e.g., in a future inline
 * edit or quick-add component).
 */
import { useState } from 'react';
import { validateTask } from '../utils/taskUtils.js';

const DEFAULT_VALUES = {
  title: '',
  description: '',
  priority: '',
  assignee: '',
  dueDate: '',
  status: 'backlog',
};

export function useTaskForm(initial = {}) {
  const [values, setValues] = useState({ ...DEFAULT_VALUES, ...initial });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const setValue = (key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const touchField = (key) => {
    setTouched((prev) => ({ ...prev, [key]: true }));
    // Re-validate on blur to clear errors as the user fixes them
    const errs = validateTask({ ...values, [key]: values[key] });
    setErrors((prev) => ({ ...prev, [key]: errs[key] }));
  };

  /**
   * Validate all fields and mark them all as touched.
   * Returns true if valid.
   */
  const validateAll = () => {
    const allTouched = { title: true, priority: true, dueDate: true };
    setTouched(allTouched);
    const errs = validateTask(values);
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const reset = () => {
    setValues({ ...DEFAULT_VALUES, ...initial });
    setErrors({});
    setTouched({});
  };

  return {
    values,
    errors,
    touched,
    setValue,
    touchField,
    validateAll,
    reset,
    /** Convenience: is a specific field invalid and touched? */
    isFieldError: (key) => Boolean(touched[key] && errors[key]),
  };
}
